import classNames from 'classnames';
import WorkAroundLegacyFieldDestroy from 'contexts/WorkAroundLegacyFieldDestroy';
import { useIntercom } from 'hooks/intercom.hook';
import { useHighlight } from 'hooks/useHighlight';
import _get from 'lodash/get';
import type { FC, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useContext, useEffect, useId, useRef } from 'react';
import type {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  RegisterOptions,
  UseFormStateReturn,
} from 'react-hook-form';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TracingConfig } from 'utils/amplitude/amplitude.types';
import { formResetOptions } from 'utils/constants';
import { QuestionMarkCircleIconOutline } from 'utils/icons';
import { Button } from '../Button';
import { Loader } from '../Loader';
import type { TooltipProps } from '../Tooltip';
import { Tooltip } from '../Tooltip';

export type FieldConnectorRenderProps = {
  error: string;
  name: string;
  label?: string;
  field: Omit<ControllerRenderProps<FieldValues, string>, 'ref'> & {
    error?: string;
    autoComplete: string;
    disabled?: boolean;
  };
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FieldValues>;
};

export type FieldConnectorProps = {
  label?: string | (() => string);
  subLabel?: string | (() => string);
  labelClassName?: string;
  mainLabelClassName?: string;
  helper?: ReactNode;
  className?: string;
  hint?: ReactNode;
  showField?: boolean;
  destroyOnHide?: boolean;
  destroyValue?: any;
  onShow?: (name: string, isUIHidden: boolean, value: any) => void;
  showUI?: boolean;
  name: string | string[];
  ErrorContext?: { [key: string]: string | number };
  children?: (renderProps: FieldConnectorRenderProps) => ReactElement<any>;
  errorKeys?: string[];
  tooltip?: TooltipProps['content'];
  horizontal?: boolean;
  PlaceholderNode?: FC;
  labelWrapperClassName?: string;
  tracing?: TracingConfig;
  displayErrorsOnFirstSubmit?: boolean;
  executeOnChange?: () => void;
  highlightID?: string;
  forceHideError?: boolean;
  disabled?: boolean | ((name: string) => boolean);
  loading?: boolean;
  setBoundingBoxField?: (field: string | null) => void;
  labelExtra?: ReactNode;
  optional?: boolean;
  showIntercomBtnWithErrors?: boolean;
  rules?: RegisterOptions<FieldValues>;
};

const runOnShowEffect = (fn: any) => {
  const id = setTimeout(fn, 32);
  return () => clearTimeout(id);
};

const runOnDestroyEffect = (fn: any) => {
  const id = setTimeout(fn, 16);
  return () => clearTimeout(id);
};

export const FieldTooltip: FC<{ content?: string }> = ({ content }) => (
  // <div className="mb-1.5">
  <Tooltip content={content} hoverable={false}>
    <QuestionMarkCircleIconOutline className="w-5 h-5 text-primary-400" />
  </Tooltip>
  // </div>
);

export const FieldErrorUI: FC<
  PropsWithChildren<{ className?: string; showIntercomBtn?: boolean }>
> = ({ className, children, showIntercomBtn }) => {
  const { t } = useTranslation();
  const { showIntercom } = useIntercom();
  return (
    <p
      className={classNames(
        'text-red-500 text-sm font-assistant leading-tight',
        className,
      )}
    >
      {children}
      {showIntercomBtn && (
        <>
          {' '}
          <Button
            asDefault
            onClick={showIntercom}
            className="font-semibold underline"
          >
            {t('contact_us_for_help')}
          </Button>
        </>
      )}
    </p>
  );
};

export const FieldErrorManager: FC<{
  className?: string;
  name: string;
  context?: Record<string, string | number>;
  showIntercomBtn?: boolean;
}> = ({ name, className, context, showIntercomBtn }) => {
  const { t } = useTranslation();
  const fieldAPI = useController({ name });

  const {
    fieldState: { error },
  } = fieldAPI;

  if (!error?.message) return null;

  return (
    <FieldErrorUI className={className} showIntercomBtn={showIntercomBtn}>
      {t(error.message, context)}
    </FieldErrorUI>
  );
};

export const FieldConnector: FC<FieldConnectorProps> = ({
  children,
  label,
  subLabel,
  labelClassName,
  labelWrapperClassName,
  mainLabelClassName,
  helper,
  hint,
  showField = true,
  destroyOnHide = true,
  showUI = true,
  destroyValue,
  name: _name,
  className: _className,
  ErrorContext,
  onShow,
  errorKeys,
  tooltip,
  PlaceholderNode,
  horizontal,
  displayErrorsOnFirstSubmit,
  executeOnChange,
  highlightID,
  forceHideError,
  disabled: propsDisabled,
  loading,
  setBoundingBoxField,
  labelExtra,
  optional,
  showIntercomBtnWithErrors,
}) => {
  const name = Array.isArray(_name) ? _name.join('_') : _name;

  const disabled =
    typeof propsDisabled === 'function' ? propsDisabled(name) : propsDisabled;

  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;

  const destroyValueRef = useRef(destroyValue);
  destroyValueRef.current = destroyValue;

  const { t } = useTranslation();
  const {
    setValue,
    reset,
    unregister,
    getValues,
    formState: { errors: formErrors, isSubmitted },
  } = useFormContext();

  const shouldUseNullAsDestroyValue = useContext(WorkAroundLegacyFieldDestroy);

  useEffect(() => {
    if (!name) return;

    if (showField && onShowRef.current) {
      return runOnShowEffect(() =>
        onShowRef.current?.(name, !showUI, getValues(name)),
      );
    }

    if (!showField && destroyOnHide) {
      return runOnDestroyEffect(() => {
        unregister(name);
        if (destroyValueRef.current !== undefined) {
          setValue(name, destroyValueRef.current);
        } else {
          if (shouldUseNullAsDestroyValue) {
            setValue(name, null);
            return;
          }
          setValue(name, undefined);
          reset(getValues(), formResetOptions);
        }
      });
    }
  }, [showField, name, unregister, setValue, destroyOnHide]);

  const highlightAPI = useHighlight();

  const className = classNames(
    _className,
    // we have the highlightAPI in each field for better control
    highlightAPI.shouldHighlight(highlightID || name) && highlightAPI.className,
  );

  if (!showField || !showUI || !children) {
    return PlaceholderNode ? <PlaceholderNode /> : null;
  }

  const errorKey = (errorKeys || ([] as string[]).concat(_name)).find((key) =>
    _get(formErrors, `${key}.message`),
  );

  const { message: fieldError, context } = errorKey
    ? _get(formErrors, errorKey)
    : ({} as any);

  const containerHorizontalClasses = classNames({
    'field-grid justify-between items-center': horizontal,
  });
  const labelClasses = classNames(
    'first-letter:capitalize font-semibold text-sm',
    labelClassName,
    'text-primary-800',
  );

  const labelContent = typeof label === 'function' ? label() : label;
  const subLabelContent =
    typeof subLabel === 'function' ? subLabel() : subLabel;

  const translatedError = t(fieldError, { ...context, ...ErrorContext, label });

  return (
    <Tooltip content={tooltip}>
      <div
        id={name} // TODO remoooove, we need to add that id on the input itself to it link with the label "htmlFor"
        data-highlight-id={highlightID || name}
        className={classNames(className, containerHorizontalClasses, {
          'field-with-error': !!fieldError,
        })}
        onMouseEnter={() => setBoundingBoxField?.(name)}
        onMouseLeave={() => setBoundingBoxField?.(null)}
      >
        <div
          className={classNames(
            'flex gap-2 items-center',
            labelWrapperClassName,
            !horizontal &&
              !!(labelExtra || labelContent || subLabelContent) &&
              'mb-1.5',
          )}
        >
          {labelContent || subLabelContent ? (
            <label htmlFor={name} className={labelClasses}>
              {labelContent && (
                <div
                  className={classNames(
                    mainLabelClassName,
                    'flex gap-2 items-center',
                  )}
                >
                  <span>
                    {labelContent}
                    {optional && (
                      <>
                        {' '}
                        <span className="text-primary-400 font-normal">
                          ({t('field.optional.tag')})
                        </span>
                      </>
                    )}
                  </span>
                  {loading ? <Loader /> : helper}
                </div>
              )}
              {subLabelContent && <div>{subLabelContent}</div>}
            </label>
          ) : null}
          {labelExtra}
        </div>
        <Controller
          name={name}
          render={({ field: { ref, ...fieldRest }, fieldState, formState }) =>
            children({
              error: translatedError as string,
              name,
              label: labelContent,
              fieldState,
              formState,
              field: {
                ...(disabled == null ? {} : { disabled }),
                ...fieldRest,
                onChange: (event: any) => {
                  executeOnChange?.();

                  return fieldRest.onChange(event);
                },
                error: translatedError as string,
                autoComplete: fieldRest.value ? 'new-password' : 'on',
              },
            })
          }
        />
        {(fieldError && !forceHideError) || hint ? (
          <div
            className={classNames({ 'grid-start-col-2-to-end': horizontal })}
          >
            {fieldError &&
              !forceHideError &&
              ((displayErrorsOnFirstSubmit && isSubmitted) ||
                !displayErrorsOnFirstSubmit) && (
                <FieldErrorUI
                  className="mt-1.5"
                  showIntercomBtn={showIntercomBtnWithErrors}
                >
                  {translatedError as ReactNode}
                </FieldErrorUI>
              )}

            {hint ? (
              <p className="flex items-center font-assistant mt-1.5 text-primary-700 text-sm leading-tight">
                {hint}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

type FakeFieldProps = {
  label?: ReactNode;
  horizontal?: boolean;
  labelClassName?: string;
  labelWrapperClassName?: string;
  helper?: ReactNode;
  className?: string;
  hint?: ReactNode;
  showField?: boolean;
  onShow?: () => void;
  children?: () => ReactElement<any>;
  disabled?: boolean;
  labelExtra?: ReactNode;
};

// same structure as FormField but without the connect
// used for ui
export const FakeField: FC<FakeFieldProps> = ({
  children,
  label,
  labelClassName,
  helper,
  hint,
  showField = true,
  className,
  onShow,
  disabled,
  horizontal,
  labelWrapperClassName,
  labelExtra,
}) => {
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;

  const name = useId();

  useEffect(() => {
    if (showField && onShowRef.current) {
      return runOnShowEffect(() => onShowRef.current?.());
    }
    //  Promise.resolve().then().then(onShowRef.current);
  }, [showField]);

  if (!showField || !children?.()) {
    return null;
  }

  const containerHorizontalClasses = classNames({
    'field-grid justify-between items-center': horizontal,
  });

  return (
    <div className={classNames(className, containerHorizontalClasses)}>
      <div
        className={classNames(
          'flex gap-2 items-center',
          labelWrapperClassName,
          label || helper || labelExtra ? 'mb-1.5' : '',
        )}
      >
        {label ? (
          <label
            htmlFor={name}
            className={classNames(
              'first-letter:capitalize font-semibold whitespace-pre-line text-sm font-assistant text-primary-800',
              labelClassName,
            )}
          >
            {label}
          </label>
        ) : null}
        {helper}
        {labelExtra}
      </div>

      {children()}

      {hint ? (
        <p className="flex items-center font-assistant mt-1.5 text-primary-700 text-sm">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
